import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
} from "react";
import { useDocumentVisibility, useLocalStorage } from "@mantine/hooks";
import UploadService from "../services/UploadService";
import { AuthContext } from "./AuthContext";
import { UPLOAD_STATUSES } from "../utils/constants";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../utils/notifications";
import AppError from "../utils/AppError";
import { useLocation } from "react-router-dom";

const camelToSnake = (str) =>
  str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

export const UploadsContext = createContext(null);

export const UploadsProvider = ({ children }) => {
  const documentState = useDocumentVisibility();
  const isDocumentVisible = documentState === "visible";
  const location = useLocation();
  const { dataScopeOrgId } = useContext(AuthContext);
  const [selectedTab, setSelectedTab] = useState("active");
  const [activePagination, setActivePagination] = useLocalStorage({
    key: "uploads-active-pagination",
    defaultValue: { pageIndex: 0, pageSize: 25 },
    getInitialValueInEffect: false,
  });
  const [completedPagination, setCompletedPagination] = useLocalStorage({
    key: "uploads-completed-pagination",
    defaultValue: { pageIndex: 0, pageSize: 25 },
    getInitialValueInEffect: false,
  });
  const [failedPagination, setFailedPagination] = useLocalStorage({
    key: "uploads-failed-pagination",
    defaultValue: { pageIndex: 0, pageSize: 25 },
    getInitialValueInEffect: false,
  });

  const activePage = activePagination.pageIndex + 1;
  const activePageSize = activePagination.pageSize;
  const completedPage = completedPagination.pageIndex + 1;
  const completedPageSize = completedPagination.pageSize;
  const failedPage = failedPagination.pageIndex + 1;
  const failedPageSize = failedPagination.pageSize;

  const [activeSearch, setActiveSearch] = useState("");
  const [completedSearch, setCompletedSearch] = useState("");
  const [failedSearch, setFailedSearch] = useState("");
  const [activeSorting, setActiveSorting] = useState([]);
  const [completedSorting, setCompletedSorting] = useState([]);
  const [failedSorting, setFailedSorting] = useState([]);

  const [activeUploads, setActiveUploads] = useState([]);
  const [activeCount, setActiveCount] = useState(null);
  const [activeLoading, setActiveLoading] = useState(false);

  const [completedUploads, setCompletedUploads] = useState([]);
  const [completedCount, setCompletedCount] = useState(null);
  const [completedLoading, setCompletedLoading] = useState(false);

  const [failedUploads, setFailedUploads] = useState([]);
  const [failedCount, setFailedCount] = useState(null);
  const [failedLoading, setFailedLoading] = useState(false);

  const dataScopeOrgIdRef = useRef(dataScopeOrgId);
  dataScopeOrgIdRef.current = dataScopeOrgId;

  const [removingUploadId, setRemovingUploadId] = useState(null);
  const [retryingUploadIds, setRetryingUploadIds] = useState(() => new Set());
  const [clearingAllFailed, setClearingAllFailed] = useState(false);

  const fetchActiveUploads = useCallback(
    async (silent = false) => {
      if (!silent) setActiveLoading(true);
      const requestOrgId = dataScopeOrgId;
      try {
        const { results, count } = await UploadService.getActiveUploads({
          target_org_id: dataScopeOrgId,
          page: activePage,
          page_size: activePageSize,
          search: activeSearch,
          ordering: activeSorting.length
            ? `${activeSorting[0].desc ? "-" : ""}${camelToSnake(activeSorting[0].id)}`
            : "",
        });
        if (requestOrgId !== dataScopeOrgIdRef.current) return;
        setActiveUploads(results);
        setActiveCount(count);
      } catch (error) {
        console.error("Error fetching active uploads", error);
      } finally {
        if (requestOrgId === dataScopeOrgIdRef.current) setActiveLoading(false);
      }
    },
    [activePage, activePageSize, activeSearch, activeSorting, dataScopeOrgId],
  );

  const fetchCompletedUploads = useCallback(
    async (silent = false) => {
      if (!silent) setCompletedLoading(true);
      const requestOrgId = dataScopeOrgId;
      try {
        const { results, count } = await UploadService.getCompletedUploads({
          target_org_id: dataScopeOrgId,
          page: completedPage,
          page_size: completedPageSize,
          search: completedSearch,
          ordering: completedSorting.length
            ? `${completedSorting[0].desc ? "-" : ""}${camelToSnake(completedSorting[0].id)}`
            : "",
        });
        if (requestOrgId !== dataScopeOrgIdRef.current) return;
        setCompletedUploads(results);
        setCompletedCount(count);
      } catch (error) {
        console.error("Error fetching completed uploads", error);
      } finally {
        if (requestOrgId === dataScopeOrgIdRef.current)
          setCompletedLoading(false);
      }
    },
    [
      completedPage,
      completedPageSize,
      completedSearch,
      completedSorting,
      dataScopeOrgId,
    ],
  );

  const fetchFailedUploads = useCallback(
    async (silent = false) => {
      if (!silent) setFailedLoading(true);
      const requestOrgId = dataScopeOrgId;
      try {
        const { results, count } = await UploadService.getActiveUploads({
          target_org_id: dataScopeOrgId,
          page: failedPage,
          page_size: failedPageSize,
          search: failedSearch,
          statuses: ["Upload Failed", "Failed To Process"],
          ordering: failedSorting.length
            ? `${failedSorting[0].desc ? "-" : ""}${camelToSnake(failedSorting[0].id)}`
            : "",
        });
        if (requestOrgId !== dataScopeOrgIdRef.current) return;
        setFailedUploads(results);
        setFailedCount(count);
      } catch (error) {
        console.error("Error fetching failed uploads", error);
      } finally {
        if (requestOrgId === dataScopeOrgIdRef.current) setFailedLoading(false);
      }
    },
    [failedPage, failedPageSize, failedSearch, failedSorting, dataScopeOrgId],
  );

  const removeProcessingUpload = useCallback(async (uploadId) => {
    try {
      setRemovingUploadId(uploadId);
      await UploadService.deleteProcessingUpload(uploadId);
      setActiveUploads((uploads) =>
        uploads.filter((upload) => upload.id !== uploadId),
      );
      setActiveCount((count) => count - 1);
      showSuccessNotification("Success", "Upload removed successfully");
    } catch (error) {
      showErrorNotification(
        new AppError({
          title: "Error",
          userMessage: "Failed to remove upload",
        }),
      );
      console.error("Error removing processing upload", error);
    } finally {
      setRemovingUploadId(null);
    }
  }, []);

  const removeFailedUpload = useCallback(async (uploadId) => {
    try {
      setRemovingUploadId(uploadId);
      await UploadService.deleteProcessingUpload(uploadId);
      setFailedUploads((uploads) =>
        uploads.filter((upload) => upload.id !== uploadId),
      );
      setFailedCount((count) => count - 1);
      showSuccessNotification("Success", "Upload removed successfully");
    } catch (error) {
      showErrorNotification(
        new AppError({
          title: "Error",
          userMessage: "Failed to remove upload",
        }),
      );
      console.error("Error removing failed upload", error);
    } finally {
      setRemovingUploadId(null);
    }
  }, []);

  const retryProcessingUpload = useCallback(
    async (upload) => {
      try {
        setRetryingUploadIds((ids) => new Set(ids).add(upload.id));
        await UploadService.retryProcessingUpload(upload);
        setActiveUploads((uploads) =>
          uploads.map((u) =>
            u.id === upload.id
              ? { ...u, status: UPLOAD_STATUSES.QUEUED.value }
              : u,
          ),
        );
        setFailedUploads((uploads) =>
          uploads.filter((u) => u.id !== upload.id),
        );
        setFailedCount((count) => count - 1);
        showSuccessNotification(
          "Success",
          `Upload "${upload?.fileName || ""}" is being retried`,
        );
      } catch (error) {
        const fileName = `"${upload?.fileName || ""}"`;
        showErrorNotification(
          new AppError({
            title: "Failed to retry upload",
            userMessage:
              error?.response?.status === 400
                ? `${fileName} can no longer be retried.`
                : `${fileName} could not be retried. Please try again.`,
          }),
        );
        console.error("Error retrying processing upload", error);
      } finally {
        setRetryingUploadIds((ids) => {
          const next = new Set(ids);
          next.delete(upload.id);
          return next;
        });
        fetchActiveUploads(true);
        fetchCompletedUploads(true);
        fetchFailedUploads(true);
      }
    },
    [fetchActiveUploads, fetchCompletedUploads, fetchFailedUploads],
  );

  const clearAllFailedUploads = useCallback(async () => {
    if (failedUploads.length === 0) return;

    try {
      setClearingAllFailed(true);

      const deletePromises = failedUploads.map((upload) =>
        UploadService.deleteProcessingUpload(upload.id),
      );

      await Promise.all(deletePromises);

      setFailedUploads([]);
      setFailedCount(0);

      showSuccessNotification(
        "Success",
        `${failedUploads.length} failed upload${
          failedUploads.length === 1 ? "" : "s"
        } cleared`,
      );

      fetchFailedUploads(true);
    } catch (error) {
      showErrorNotification(
        new AppError({
          title: "Error",
          userMessage: "Failed to clear some uploads",
        }),
      );
      console.error("Error clearing failed uploads", error);
    } finally {
      setClearingAllFailed(false);
    }
  }, [failedUploads, fetchFailedUploads]);

  useEffect(() => {
    setActivePagination((p) => ({ ...p, pageIndex: 0 }));
  }, [activeSearch]);

  useEffect(() => {
    setCompletedPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [completedSearch]);

  useEffect(() => {
    setFailedPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [failedSearch]);

  useEffect(() => {
    setActiveCount(null);
    setCompletedCount(null);
    setFailedCount(null);
  }, [dataScopeOrgId]);

  useEffect(() => {
    fetchActiveUploads();
  }, [fetchActiveUploads]);

  useEffect(() => {
    fetchCompletedUploads();
  }, [fetchCompletedUploads]);

  useEffect(() => {
    fetchFailedUploads();
  }, [fetchFailedUploads]);

  useEffect(() => {
    if (activeCount > 0 && location.pathname === "/uploads") {
      if (!isDocumentVisible) {
        return;
      }

      fetchActiveUploads(true);
      fetchCompletedUploads(true);
      fetchFailedUploads(true);

      const intervalId = setInterval(() => {
        fetchActiveUploads(true);
        fetchCompletedUploads(true);
        fetchFailedUploads(true);
      }, 10000);

      return () => clearInterval(intervalId);
    }
  }, [
    activeCount,
    fetchActiveUploads,
    fetchCompletedUploads,
    fetchFailedUploads,
    location.pathname,
    isDocumentVisible,
  ]);

  return (
    <UploadsContext.Provider
      value={{
        activeUploads,
        activeCount,
        activeLoading,
        activePage,
        activePagination,
        setActivePagination,
        activeSearch,
        setActiveSearch,
        fetchActiveUploads,
        completedUploads,
        completedCount,
        completedLoading,
        completedPage,
        completedPagination,
        setCompletedPagination,
        completedSearch,
        setCompletedSearch,
        fetchCompletedUploads,
        failedUploads,
        failedCount,
        failedLoading,
        failedPage,
        failedPagination,
        setFailedPagination,
        failedSearch,
        setFailedSearch,
        fetchFailedUploads,
        selectedTab,
        setSelectedTab,
        activeSorting,
        setActiveSorting,
        completedSorting,
        setCompletedSorting,
        failedSorting,
        setFailedSorting,
        removeProcessingUpload,
        removeFailedUpload,
        removingUploadId,
        retryProcessingUpload,
        retryingUploadIds,
        clearAllFailedUploads,
        clearingAllFailed,
      }}
    >
      {children}
    </UploadsContext.Provider>
  );
};
